import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, UserCheck, FileCheck, DollarSign, AlertCircle, TrendingUp, Trophy, AlertTriangle, Megaphone, Loader2, Undo2, Clock, Bell, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOverview = ({ stats }) => {
  const navigate = useNavigate();
  const [bizStats, setBizStats] = useState(null);
  const [bulkAdvertRunning, setBulkAdvertRunning] = useState(false);
  const [returnedBooks, setReturnedBooks] = useState([]);
  const [returnedLoading, setReturnedLoading] = useState(true);
  const [nudging, setNudging] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard-stats`).then(r => setBizStats(r.data)).catch(() => {});
    fetchReturnedBooks();
  }, []);

  const fetchReturnedBooks = () => {
    setReturnedLoading(true);
    axios.get(`${API}/proofreading/recently-returned?limit=30`)
      .then(r => setReturnedBooks(r.data || []))
      .catch(() => {})
      .finally(() => setReturnedLoading(false));
  };

  const handleFollowUp = async (book) => {
    if (!window.confirm(`Stuur ’n vriendelike herinnering aan ${book.skrywer_naam || 'die skrywer'} om "${book.boek_naam}" te herlaai?`)) return;
    setNudging(book.id);
    try {
      const r = await axios.post(`${API}/proofreading/follow-up-writer/${book.id}`);
      const n = r.data.nudge_count;
      const emailNote = r.data.email_sent ? '' : ' (e-pos was nie gekonfigureer nie)';
      toast.success(`Herinnering gestuur. Totaal: ${n} opvolg${n === 1 ? '' : 'e'}${emailNote}.`);
      fetchReturnedBooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie opvolg nie.');
    } finally {
      setNudging(null);
    }
  };

  const handleBulkCreateAdverts = async () => {
    if (!window.confirm('Skep konsep-advertensies vir alle boeke wat in die afgelope 7 dae gepubliseer is? Bestaande konsepte word oorgeslaan.')) return;
    setBulkAdvertRunning(true);
    try {
      const r = await axios.post(`${API}/admin/adverts/bulk-create-from-recent-books?days=7`);
      const { created, skipped_existing } = r.data;
      if (created === 0 && skipped_existing === 0) {
        toast.info('Geen nuwe boeke in die afgelope 7 dae gepubliseer nie.');
      } else {
        const skipMsg = skipped_existing > 0 ? ` (${skipped_existing} oorgeslaan — konsep bestaan reeds)` : '';
        toast.success(`${created} konsep-advertensie(s) geskep${skipMsg}.`);
        if (created > 0) {
          setTimeout(() => navigate('/admin/adverteer'), 800);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie konsepte skep nie.');
    } finally {
      setBulkAdvertRunning(false);
    }
  };

  const fmt = (n) => `R ${Number(n || 0).toFixed(2)}`;
  const delta = bizStats
    ? bizStats.current_month.total_revenue - bizStats.previous_month.total_revenue
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Admin Oorsig</h2>
          <p className="text-gray-600">Stelsel statistieke en hangende aksies</p>
        </div>
        <Button
          onClick={handleBulkCreateAdverts}
          disabled={bulkAdvertRunning}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
          data-testid="bulk-create-adverts-btn"
          title="Skep dadelik 'n konsep-advert vir elke boek wat in die afgelope 7 dae gepubliseer is"
        >
          {bulkAdvertRunning ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Megaphone className="h-4 w-4 mr-2" />
          )}
          Skep Adverte vir Nuwe Boeke
        </Button>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Totale Statistieke</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard icon={<BookOpen />} title="Totale Boeke" value={stats?.total_books || 0} color="from-blue-400 to-blue-600" />
          <StatCard icon={<Users />} title="Skrywers" value={stats?.total_writers || 0} color="from-green-400 to-green-600" />
          <StatCard icon={<UserCheck />} title="Proeflees" value={stats?.total_proofreaders || 0} color="from-purple-400 to-purple-600" />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hangende Aksies</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard icon={<FileCheck />} title="Wag Goedkeuring" value={stats?.pending_approvals || 0} color="from-orange-400 to-orange-600" alert />
          <StatCard icon={<DollarSign />} title="Hangende Betalings" value={stats?.pending_payments || 0} color="from-yellow-400 to-yellow-600" alert />
          <StatCard icon={<AlertCircle />} title="Nuwe Aansoeke" value={stats?.pending_proofreaders || 0} color="from-red-400 to-red-600" alert />
        </div>
      </div>

      {bizStats && (
        <>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" /> Inkomste Vergelyking
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card><CardContent className="pt-6">
                <p className="text-sm text-gray-600">{bizStats.current_month.label} (Lopend)</p>
                <p className="text-3xl font-bold text-emerald-700">{fmt(bizStats.current_month.total_revenue)}</p>
                <p className="text-xs text-gray-500 mt-1">{bizStats.current_month.total_purchases} aankope · {bizStats.current_month.total_rentals} huur</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-sm text-gray-600">{bizStats.previous_month.label}</p>
                <p className="text-3xl font-bold text-gray-700">{fmt(bizStats.previous_month.total_revenue)}</p>
                <p className="text-xs text-gray-500 mt-1">{bizStats.previous_month.total_purchases} aankope · {bizStats.previous_month.total_rentals} huur</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-sm text-gray-600">Verskil</p>
                <p className={`text-3xl font-bold ${delta >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {delta >= 0 ? '+' : ''}{fmt(delta)}
                </p>
                <p className="text-xs text-gray-500 mt-1">vs vorige maand</p>
              </CardContent></Card>
            </div>
          </div>

          {bizStats.top_books.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" /> Top 5 Boeke ({bizStats.current_month.label})
              </h3>
              <Card><CardContent className="pt-4">
                <table className="w-full text-sm" data-testid="top-books-table">
                  <thead><tr className="border-b text-gray-500">
                    <th className="text-left py-2">#</th><th className="text-left py-2">Boek</th>
                    <th className="text-right py-2">Aankope</th><th className="text-right py-2">Huur</th>
                    <th className="text-right py-2">Inkomste</th>
                  </tr></thead>
                  <tbody>
                    {bizStats.top_books.map((b, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2">{i + 1}</td>
                        <td className="py-2 font-medium">{b._id}</td>
                        <td className="py-2 text-right">{b.purchases}</td>
                        <td className="py-2 text-right">{b.rentals}</td>
                        <td className="py-2 text-right font-semibold text-emerald-700">{fmt(b.total_rand)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            </div>
          )}

          {bizStats.top_writers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-purple-500" /> Top 3 Skrywers ({bizStats.current_month.label})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {bizStats.top_writers.map((w, i) => (
                  <Card key={i}><CardContent className="pt-6">
                    <p className="text-xs text-gray-500">#{i + 1}</p>
                    <p className="text-lg font-bold">{w.writer_name}</p>
                    <p className="text-emerald-700 font-semibold mt-1">{fmt(w.total_payout)} verdienste</p>
                  </CardContent></Card>
                ))}
              </div>
            </div>
          )}

          {bizStats.no_faults_metric && bizStats.no_faults_metric.total_published > 0 && (
            <div data-testid="no-faults-metric-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-600" /> Geen Foute Gevind — Skrywerkwaliteit
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Card className="border-2 border-emerald-200 bg-emerald-50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-emerald-900">Foutvry gepubliseer</p>
                    <p className="text-4xl font-bold text-emerald-700" data-testid="no-faults-count">
                      {bizStats.no_faults_metric.no_faults_count}
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      van {bizStats.no_faults_metric.total_published} gepubliseerde boeke
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-emerald-200 bg-white">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600">Foutvry %</p>
                    <p className="text-4xl font-bold text-emerald-700" data-testid="no-faults-percentage">
                      {bizStats.no_faults_metric.percentage}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">van alle gepubliseerde boeke</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-2">Wenk</p>
                    <p className="text-sm text-gray-700 leading-snug">
                      Spotlig hierdie skrywers in jou sosiale media adverte — &ldquo;Foutvry by die eerste lees!&rdquo;
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => navigate('/admin/adverteer')}
                      data-testid="no-faults-cta-adverteer"
                    >
                      <Megaphone className="h-4 w-4 mr-1" /> Gaan na Adverteer
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {bizStats.no_faults_metric.top_writers && bizStats.no_faults_metric.top_writers.length > 0 && (
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Top skrywers met foutvry boeke
                    </p>
                    <ul className="space-y-2 text-sm" data-testid="no-faults-top-writers">
                      {bizStats.no_faults_metric.top_writers.map((w, i) => (
                        <li key={w._id || i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-2 border-b last:border-0">
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-900">
                              #{i + 1} {w.writer_name || 'Onbekend'}
                            </span>
                            {w.sample_books && w.sample_books.length > 0 && (
                              <p className="text-xs text-gray-500 italic mt-0.5 truncate">
                                {w.sample_books.join(' · ')}
                              </p>
                            )}
                          </div>
                          <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0">
                            {w.flawless_count} foutvry
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {bizStats.zero_sales_count > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" /> Boeke sonder verkope hierdie maand ({bizStats.zero_sales_count})
              </h3>
              <Card><CardContent className="pt-4">
                <p className="text-sm text-gray-600 mb-3">Hierdie gepubliseerde boeke het nog geen aankope of huur hierdie maand nie. Oorweeg om ’n advert te skep!</p>
                <ul className="space-y-1 text-sm">
                  {bizStats.zero_sales_books.map(b => (
                    <li key={b.id} className="flex justify-between py-1 border-b last:border-0">
                      <span>{b.boek_naam}</span><span className="text-gray-500">{b.skrywer_naam}</span>
                    </li>
                  ))}
                </ul>
              </CardContent></Card>
            </div>
          )}
        </>
      )}

      {/* ============ ONLANGS TERUG ============ */}
      <div data-testid="recently-returned-section">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-amber-600" />
          Onlangs Terug na Skrywers
          {returnedBooks.filter(b => b.outstanding).length > 0 && (
            <span className="ml-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid="outstanding-count-badge">
              {returnedBooks.filter(b => b.outstanding).length} hangend
            </span>
          )}
        </h3>
        <Card>
          <CardContent className="pt-4">
            {returnedLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
            ) : returnedBooks.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="recently-returned-empty">
                Geen boeke is onlangs teruggestuur nie.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {returnedBooks.map(b => (
                  <li key={b.id} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 py-2 border-b last:border-0"
                      data-testid={`returned-row-${b.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 truncate">{b.boek_naam}</span>
                        <span className="text-gray-500 text-xs">deur {b.skrywer_naam || 'onbekend'}</span>
                        {b.outstanding ? (
                          <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded inline-flex items-center gap-1"
                                data-testid={`returned-badge-${b.id}`}
                                title="Skrywer het nog nie ’n regstelling opgelaai nie">
                            <Clock className="h-3 w-3" /> Wag op skrywer
                          </span>
                        ) : (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                            Herlaai voltooi
                          </span>
                        )}
                        {b.nudge_count > 0 && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded inline-flex items-center gap-1"
                                data-testid={`nudge-count-${b.id}`}
                                title={b.last_nudge_at ? `Laaste opvolg: ${new Date(b.last_nudge_at).toLocaleString('af-ZA')}` : ''}>
                            <Bell className="h-3 w-3" /> {b.nudge_count} opvolg{b.nudge_count === 1 ? '' : 'e'}
                          </span>
                        )}
                      </div>
                      {b.return_reason && (
                        <p className="text-xs text-gray-600 mt-1 italic">&ldquo;{b.return_reason}&rdquo;</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Teruggestuur deur {b.returned_by_name || 'admin'}
                        {b.days_since !== null && (
                          <> · {b.days_since === 0 ? 'vandag' : b.days_since === 1 ? 'gister' : `${b.days_since} dae gelede`}</>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {b.outstanding && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-blue-500 text-blue-700 hover:bg-blue-50"
                          onClick={() => handleFollowUp(b)}
                          disabled={nudging === b.id}
                          data-testid={`follow-up-btn-${b.id}`}
                          title="Stuur ’n vriendelike herinnering aan die skrywer (bel + e-pos)"
                        >
                          {nudging === b.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Bell className="h-3 w-3 mr-1" />
                          )}
                          Volg op met skrywer
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/admin/books')}
                        className="text-xs"
                        data-testid={`returned-view-${b.id}`}
                      >
                        Sien in Boeke
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, color, alert }) => (
  <Card className={alert && value > 0 ? 'border-2 border-orange-300' : ''}>
    <CardContent className="pt-6">
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center text-white`}>
          {React.cloneElement(icon, { className: 'h-8 w-8' })}
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default AdminOverview;